import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GBELogo } from "@/components/layout/GBELogo";
import { Loader2, AlertOctagon, CheckCircle2, KeyRound, MailCheck } from "lucide-react";
import { toast } from "sonner";


export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      // Backend is anti-enumeration; treat any failure as success from UX perspective
      setSent(true);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center p-6">
      <div className="w-full max-w-[440px]">
        <div className="mb-8 flex justify-center">
          <div className="rounded-lg px-4 py-3 bg-gbe"><GBELogo /></div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-8">
          {!sent ? (
            <>
              <div className="mb-6">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gbe font-medium">
                  <KeyRound className="w-4 h-4" /> Forgot password
                </div>
                <h1 className="font-display text-2xl font-semibold text-slate-900 mt-2">Reset your password</h1>
                <p className="text-sm text-slate-500 mt-1">
                  Enter the email address on your account and we'll send you a link to set a new password.
                </p>
              </div>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-slate-700">Email</Label>
                  <Input
                    id="email"
                    data-testid="forgot-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1.5"
                    placeholder="you@company.com"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="forgot-submit"
                  disabled={busy || !email}
                  className="w-full bg-gbe hover:bg-gbe-hover text-white h-10"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
                </Button>
              </form>
              <div className="mt-6 text-center text-sm text-slate-500">
                <Link to="/login" className="text-gbe hover:text-gbe font-medium">← Back to sign in</Link>
              </div>
            </>
          ) : (
            <div className="text-center py-4" data-testid="forgot-sent">
              <MailCheck className="w-10 h-10 text-emerald-600 mx-auto" />
              <h1 className="font-display text-xl font-semibold text-slate-900 mt-3">Check your inbox</h1>
              <p className="text-sm text-slate-500 mt-2">
                If an account exists for <span className="font-medium text-slate-700">{email}</span>, we've
                sent a password-reset link. It's valid for 60 minutes.
              </p>
              <Link to="/login" className="inline-block mt-6 text-gbe hover:text-gbe font-medium text-sm">← Back to sign in</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [status, setStatus] = useState("loading"); // loading | valid | invalid | done
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }
    api.get(`/auth/reset-password/${token}`).then((r) => {
      if (r.data.valid) { setEmail(r.data.email); setStatus("valid"); }
      else setStatus("invalid");
    }).catch(() => setStatus("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) { toast.error("Passwords don't match"); return; }
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await api.post(`/auth/reset-password/${token}`, { password: pw });
      setStatus("done");
      toast.success("Password updated");
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center p-6">
      <div className="w-full max-w-[440px]">
        <div className="mb-8 flex justify-center">
          <div className="rounded-lg px-4 py-3 bg-gbe"><GBELogo /></div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-8">
          {status === "loading" && (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          )}
          {status === "invalid" && (
            <div className="text-center" data-testid="reset-invalid">
              <AlertOctagon className="w-8 h-8 text-rose-600 mx-auto" />
              <h1 className="font-display text-xl font-semibold text-slate-900 mt-3">Link invalid or expired</h1>
              <p className="text-sm text-slate-500 mt-2">Request a fresh reset link to continue.</p>
              <Link to="/forgot-password" className="inline-block mt-6 text-gbe hover:text-gbe font-medium text-sm">Request a new link</Link>
            </div>
          )}
          {status === "valid" && (
            <>
              <div className="mb-6">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gbe font-medium">
                  <KeyRound className="w-4 h-4" /> Set new password
                </div>
                <h1 className="font-display text-2xl font-semibold text-slate-900 mt-2">Reset your password</h1>
                <p className="text-sm text-slate-500 mt-1">For <span className="font-medium text-slate-700">{email}</span></p>
              </div>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="pw" className="text-slate-700">New password</Label>
                  <Input
                    id="pw"
                    data-testid="reset-pw"
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    required
                    className="mt-1.5"
                    placeholder="Min. 8 characters, letter + digit"
                  />
                </div>
                <div>
                  <Label htmlFor="pw2" className="text-slate-700">Confirm new password</Label>
                  <Input
                    id="pw2"
                    data-testid="reset-pw-confirm"
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className="mt-1.5"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="reset-submit"
                  disabled={busy || pw.length < 8 || pw !== confirm}
                  className="w-full bg-gbe hover:bg-gbe-hover text-white h-10"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
                </Button>
              </form>
            </>
          )}
          {status === "done" && (
            <div className="text-center py-4" data-testid="reset-done">
              <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
              <h1 className="font-display text-xl font-semibold text-slate-900 mt-3">Password updated</h1>
              <p className="text-sm text-slate-500 mt-2">Redirecting you to sign in…</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
